import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { Loader } from "rsuite";
import { TbHandClick } from "react-icons/tb";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { axInst } from "../../../Components/Login/Service";

/* ================= REGION ================= */
const region_table_details = async (body) => {
  try {
    const res = await axInst.post("/realization/region_wise_realization", body);
    const obj = res.data;

    const REGION_ORDER = ["North", "East", "West", "South"];

    const formattedData = REGION_ORDER.filter((region) => obj[region]) // keep only available regions
      .map((region) => ({
        Region: region,
        MOP: obj[region].MOP,
        Realization: obj[region].Realization,
        Discount: obj[region].Discount,
      }));

    return { obj: formattedData };
  } catch (error) {
    console.error(error);
    return { obj: [] };
  }
};

/* ================= AREA MANAGER ================= */
const area_manager_table_details = async (requestBody) => {
  try {
    const res = await axInst.post(
      "/realization/area_manager_wise_realization",
      requestBody,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

/* ================= DEALER ================= */
const dealerwise_table_details = async (requestBody) => {
  try {
    const res = await axInst.post(
      "/realization/dealer_wise_realization",
      requestBody,
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const TqpStatus = () => {
  const { data: filterdata, submitted } = useSelector(
    (state) => state.filtersRealization,
  );

  const { data, isFetching, isError } = useQuery({
    queryKey: ["realizationTable", submitted],
    queryFn: () => region_table_details(filterdata),
    refetchInterval: 300000,
    cacheTime: 0,
    staleTime: 0,
    refetchIntervalInBackground: true,
  });

  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState("");
  const [expandedRows, setExpandedRows] = useState({});

  useEffect(() => {
    return () => setExpandedRows({});
  }, []);

  /* ================= EXPAND LOGIC ================= */
  const toggleRowExpanded = async (region, areaManager = null) => {
    try {
      if (areaManager) {
        const requestBody = {
          ...filterdata,
          region: [region],
          Spm_Name: [areaManager],
        };

        if (!expandedRows[region]?.[areaManager]?.isExpanded) {
          const dealerData = await dealerwise_table_details(requestBody);

          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [areaManager]: {
                ...prev[region]?.[areaManager],
                isExpanded: true,
                dealerData,
              },
            },
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: {
              ...prev[region],
              [areaManager]: {
                ...prev[region]?.[areaManager],
                isExpanded: false,
              },
            },
          }));
        }
      } else {
        if (!expandedRows[region]) {
          const requestBody = {
            ...filterdata,
            region: [region],
          };

          const amData = await area_manager_table_details(requestBody);

          const mapped = Object.keys(amData).reduce((acc, key) => {
            acc[key] = {
              isExpanded: false,
              data: amData[key],
            };
            return acc;
          }, {});

          setExpandedRows((prev) => ({
            ...prev,
            [region]: mapped,
          }));
        } else {
          setExpandedRows((prev) => ({
            ...prev,
            [region]: false,
          }));
        }
      }
    } catch (error) {
      console.error("Error toggling row expansion:", error);
    }
  };

  /* ================= COLUMNS ================= */
  const columns = [
    {
      header: "Region",
      accessorFn: (row) => row.Region,
    },

    {
      header: "MOP (₹)",
      accessorFn: (row) => row.MOP,
    },
    {
      header: "Realization %",
      accessorFn: (row) => row.Realization,
    },
    {
      header: "Discount (₹)",
      accessorFn: (row) => row.Discount,
    },
  ];

  const table = useReactTable({
    data: data?.obj ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  /* ================= UI ================= */
  return (
    <div className="p-2 relative">
      <header className="pb-2 text-sm font-semibold text-[#545454]">
        Region Wise Realization
      </header>

      <div className="overflow-auto h-[198px]">
        {isError && (
          <div className="grid place-items-center h-full text-sm">
            Error Occurred
          </div>
        )}

        {isFetching && (
          <div className="grid place-items-center h-full">
            <Loader />
          </div>
        )}

        {!isFetching && !isError && (
          <>
            <WaterMark />

            <table className="w-full text-xs border-collapse text-center">
              <thead className="sticky text-white top-0 bg-[#2A5775] text-left z-[1]">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-3 text-sm py-2 text-center font-medium border border-[#355f7a] whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row, rowIndex) => (
                  <React.Fragment key={row.id}>
                    {/* REGION */}
                    <tr
                      className={`even:bg-gray-200 odd:bg-white ${
                        rowIndex === 0 ? "cursor-default" : "cursor-pointer"
                      } ${
                        expandedRows[row.original.Region] ? "font-bold" : ""
                      }`}
                      onClick={() => toggleRowExpanded(row.original.Region)}
                    >
                      {row.getVisibleCells().map((cell, idx) => (
                        <td
                          key={cell.id}
                          className="p-[2px] text-xs  lg:text-sm border border-gray-300 h-[30px] relative"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}

                          {idx === 0 && (
                            <span className="absolute right-1 top-1/2 -translate-y-1/2 text-slate-500">
                              {expandedRows[row.original.Region] ? (
                                <IoMdArrowDropup size={16} />
                              ) : (
                                <IoMdArrowDropdown size={16} />
                              )}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>

                    {/* AREA MANAGER */}
                    {expandedRows[row.original.Region] &&
                      Object.entries(expandedRows[row.original.Region]).map(
                        ([am, amDetails]) => (
                          <React.Fragment key={am}>
                            <tr
                              className={`bg-[#2A5775] text-white ${
                                amDetails.isExpanded
                                  ? "font-bold"
                                  : "font-normal"
                              }`}
                              // className="bg-[#2a5775]  text-white hover:bg-[#244b65] cursor-pointer transition"
                              onClick={() =>
                                toggleRowExpanded(row.original.Region, am)
                              }
                            >
                              <td className="p-1 text-[12px] border border-gray-300 h-8 relative">
                                <span className="ml-4">{am}</span>
                                <div className="absolute left-[8rem] top-2">
                                  {amDetails.isExpanded ? (
                                    <IoMdArrowDropup size={14} />
                                  ) : (
                                    <IoMdArrowDropdown size={14} />
                                  )}
                                </div>
                              </td>

                              <td className="p-1 pl-6  text-left  border border-gray-300 h-6 whitespace-nowrap">
                                {amDetails.data.MOP}
                              </td>
                              <td className="p-1 pl-6  text-left  border border-gray-300 h-6 whitespace-nowrap">
                                {amDetails.data.Realization}
                              </td>
                              <td className="p-1 pl-6  text-left border border-gray-300 h-6 whitespace-nowrap ">
                                {amDetails.data.Discount}
                              </td>
                            </tr>

                            {/* DEALER */}
                            {amDetails.isExpanded &&
                              Object.entries(amDetails.dealerData || {}).map(
                                ([dealer, d]) => (
                                  <tr
                                    key={dealer}
                                    className="bg-slate-100 text-slate-700  even:bg-gray-50 odd:bg-white text-[10px] hover:bg-slate-200 transition"
                                  >
                                    <td className="p-1 text-center lg:text-sm border border-gray-300 h-6">
                                      {dealer}
                                    </td>
                                    <td className="p-1  text-left lg:text-sm border border-gray-300 h-6">
                                      {d.MOP}
                                    </td>
                                    <td className="p-1 text-left lg:text-sm border border-gray-300 h-6">
                                      {d.Realization}
                                    </td>
                                    <td className="p-1  text-left lg:text-sm border border-gray-300 h-6">
                                      {d.Discount}
                                    </td>
                                  </tr>
                                ),
                              )}
                          </React.Fragment>
                        ),
                      )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            <p className="text-[11px] mt-2 flex items-center gap-1 text-slate-600">
              <TbHandClick size={14} />
              Click on Region to view Area Manager & Dealer details
            </p>
          </>
        )}
      </div>
    </div>
  );
};
